import { useParams } from 'react-router-dom';
import { query, where } from 'firebase/firestore';
import ClockIcon from 'components/icons/time';
import Button from 'components/Button';
import InboundCallIcon from 'components/icons/inboundCall';
import OutboundCallIcon from 'components/icons/outboundCall';
import useCustomers from 'data/firestore/agent/customer/useCollection';
import { useEffect, useState } from 'react';
import {
    CallItem,
    CallUserInfo,
    CallMetadata,
    CallTypeContainer,
    CallTypeIcon,
    CallActions,
    CallTimerDisplay,
    StyledCustomersInfo,
} from './styled';
import { ActiveCall } from './types';
import extractPhoneNumber from './helpers/extractPhoneNumber';

// Simplified real-time timer component
const RealTimeTimer = ({ startTime }: { startTime: Date }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            const elapsedMs = now.getTime() - startTime.getTime();
            setElapsed(Math.floor(elapsedMs / 1000));
        }, 1000);

        return () => clearInterval(interval);
    }, [startTime]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <CallTimerDisplay>
            <ClockIcon />
            {formatTime(elapsed)}
        </CallTimerDisplay>
    );
};

const CallElement = ({
    call,
    onCallClick = () => {},
    onViewCall = () => {},
}: {
    call: ActiveCall,
    onCallClick?: (call: ActiveCall) => void;
    onViewCall?: (callId: string) => void;
}) => {
    const { agentId = '' } = useParams<{ agentId?: string }>();

    const { customer: customerInfo, type } = call;
    const phoneNumber = extractPhoneNumber(customerInfo) || '';
    const icon = type === 'inbound' ? <InboundCallIcon /> : <OutboundCallIcon />;

    const { data: customers = [], isLoading } = useCustomers(agentId, {
        listQueryFn: (ref) => query(ref, where('phoneNumber', '==', phoneNumber)),
        disabled: !!customerInfo?.id,
    });

    const mappedCustomers = (
        customerInfo?.id
            ? [{
                ...customerInfo,
                phoneNumber: extractPhoneNumber(customerInfo),
                role: 'customer' as const,
            }]
            : (customers || [])
    ).map((c) => ({ ...c, role: 'customer' as const }));

    const handleViewCall = (callId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        onViewCall?.(callId);
    };

    return (
        <CallItem
            key={call.id}
            onClick={() => onCallClick(call)}
        >
            <CallUserInfo>
                <StyledCustomersInfo
                    customers={mappedCustomers}
                    isLoading={isLoading}
                    maxVisible={1}
                />
            </CallUserInfo>

            <CallMetadata>
                <CallTypeContainer>
                    <CallTypeIcon>
                        {icon}
                    </CallTypeIcon>
                </CallTypeContainer>

                <RealTimeTimer startTime={call.startTime} />
            </CallMetadata>

            <CallActions>
                <Button
                    size="small"
                    variant="base"
                    borderRadius="semi-rounded"
                    onClick={(event) => handleViewCall(call.id, event)}
                    label="View"
                />
            </CallActions>
        </CallItem>
    );
};

export default CallElement;
